import {
  OrderDetails,
  OrderLineItem,
  OrderShippingAddress,
  ShippingPreference,
} from "../../../api/backend/backend-api"
import { toProduct } from "../../../converters/catalog"
import { ProductVariant } from "../../../types/product"
import { ShippingAddress } from "../../checkout/state/checkoutTypes"
import { toBasketPromo } from "../../checkout/state/converters/toBasket"
import { CompletedOrder, OrderItem } from "../types"

const toShippingAddress = (
  address?: OrderShippingAddress
): ShippingAddress => ({
  address: address?.address ?? "",
  address2: address?.address2 ?? "",
  city: address?.city ?? "",
  country: "",
  countryCode: address?.countryCode ?? "",
  firstName: address?.firstName ?? "",
  lastName: address?.lastName ?? "",
  phone: address?.phone ?? "",
  province: "",
  provinceCode: address?.provinceCode ?? "",
  zip: address?.zipCode ?? "",
})

const toLineItem = (
  order: OrderDetails,
  lineItem: OrderLineItem
): OrderItem => {
  const product = lineItem.product
    ? toProduct(lineItem.product.document)
    : undefined
  const variant = product?.variants.find(
    (x) => x.info.variantId === lineItem.product?.variant.info.variantId
  ) as ProductVariant
  return {
    type: lineItem.assignedGiftCardCode ? "giftcard" : "product",
    assignedGiftCardCode: lineItem.assignedGiftCardCode ?? undefined,
    shopifyLineItemId: lineItem.id,
    product,
    variant,
    order: {
      gift: lineItem.isGift,
      quantity: lineItem.quantity,
      total: {
        discountedPrice: lineItem.discountedTotal,
        originalPrice: lineItem.originalTotal,
        compareAtPrice: lineItem.originalTotalCompareAtPrice ?? undefined,
        currency: order.price.currency,
      },
    },
  }
}

export const toConfirmedOrder = (order: OrderDetails): CompletedOrder => ({
  customer: {
    email: order.customer.email ?? undefined,
    acceptsMarketing: order.customer.acceptsMarketing ?? false,
  },
  id: order.id,
  alias: order.alias as string,
  status: order.status,
  createdAt: order.createdAt,
  items: order.lineItems.map((x) => toLineItem(order, x)),
  giftItems: order.giftLineItems?.map((x) => toLineItem(order, x)) ?? [],
  recap: {
    total: {
      currency: order.price.currency,
      price: order.price.total,
    },
    totalToPay: {
      currency: order.price.currency,
      price: order.price.totalPaid,
    },
    articlesNumber: order.lineItems.length,
    shipping: {
      currency: order.price.currency,
      price: order.price.shippingPrice as number,
    },
    subtotal: {
      currency: order.price.currency,
      price: order.price.subtotal,
    },
    discountTotal: order.price.discountTotal
      ? {
          currency: order.price.currency,
          price: order.price.discountTotal,
        }
      : undefined,
    giftcards:
      order.price.giftcards?.map((x) => ({
        code: x.code,
        usedAmount: x.amountUsed,
      })) ?? [],
    basketDiscounts: order.basketDiscounts.map((x) => toBasketPromo(x)),
  },
  shipping: {
    shippingAddress: toShippingAddress(
      order.shipping?.shippingAddress as OrderShippingAddress
    ),
    billingAddress: toShippingAddress(
      order.shipping?.billingAddress as OrderShippingAddress
    ),
    shippingMethod:
      order.shipping?.preference === ShippingPreference.ClickAndCollect
        ? "store"
        : "courier",
    store: order.shipping?.store
      ? {
          id: order.shipping.store.id,
          company: order.shipping.store.company,
          address: order.shipping.store.address.street,
          cap: order.shipping.store.address.cap,
          city: order.shipping.store.address.city,
          province: order.shipping.store.address.province,
          name: order.shipping.store.name,
        }
      : undefined,
  },
  promotions:
    order.promotions?.map((x) => ({
      id: x.id,
      code: x.code,
      discount: x.amount,
      trigger: x.trigger,
    })) ?? [],
  tracking: order.tracking
    ? {
        url: order.tracking.url,
      }
    : undefined,
  delivery: order.deliveredDate
    ? {
        deliveredDate: order.deliveredDate as string,
        shippedDate: order.shippedDate as string,
      }
    : undefined,
  editOrderPendingPayment:
    order.editOrder && (order.editOrder.pendingPaymentAmount ?? 0) > 0
      ? {
          amount: order.editOrder.pendingPaymentAmount as number,
          url: order.editOrder.pendingPaymentUrl as string,
        }
      : undefined,
})
